import React from "react";
import { Box, TableBody, TableCell, TableRow } from "@mui/material";
import { NoDataStub } from "@components";
import { LoadingTableRow } from "../LoadingTableRow.tsx";
import { GroupByConfig, TableConfig } from "../types.ts";
import { GroupedData } from "./useGroupedData.ts";
import { AutoTableRow } from "./AutoTableRow.tsx";

type RowBase = { children?: any[] };

type Props<Data extends RowBase> = {
  settings: TableConfig<Data>;
  isPending: boolean;
  showNoData?: boolean;
  noDataFirstLine: string;
  noDataSecondLine?: string;
  groupedData: GroupedData<Data> | null;
  pagedData: Data[];
  groupBy?: GroupByConfig<Data>;
  hasExpandableRows: boolean;
  additionalProps?: Record<string, unknown>;
};

const defaultGroupHeader = (label: string, key: string | number) => (
  <TableRow data-group={key}>
    <TableCell colSpan={20} sx={{ p: "0 !important", borderBottom: "none" }}>
      <Box
        mt={1}
        py={4}
        px={3}
        sx={{
          backgroundColor: "background.subtle",
          borderRadius: 1,
        }}
      >
        <Box fontWeight={600} typography="body2">
          {label}
        </Box>
      </Box>
    </TableCell>
  </TableRow>
);

export function AutoTableBody<Data extends RowBase>({
  settings,
  isPending,
  showNoData,
  noDataFirstLine,
  noDataSecondLine,
  groupedData,
  pagedData,
  groupBy,
  hasExpandableRows,
  additionalProps,
}: Props<Data>) {
  if (isPending) {
    return (
      <TableBody>
        {Array(settings.loadingRows ?? 5)
          .fill(undefined)
          .map((_, index) => (
            <LoadingTableRow settings={settings} key={index} />
          ))}
      </TableBody>
    );
  }

  if (showNoData) {
    return (
      <TableBody>
        <TableRow data-testid="auto-table-no-data-row">
          <TableCell colSpan={20}>
            <NoDataStub
              firstLine={noDataFirstLine}
              secondLine={noDataSecondLine ?? ""}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  if (groupedData) {
    const renderHeader = groupBy?.headerRenderer ?? defaultGroupHeader;
    return (
      <TableBody>
        {groupedData.map((group) => (
          <React.Fragment key={group.key}>
            {renderHeader(group.label, group.key)}
            {group.items.map((item, i) => {
              const rowKey = settings.getRowId?.(item) ?? i;
              const isLast = i === group.items.length - 1;
              return (
                <AutoTableRow
                  key={rowKey}
                  settings={settings}
                  row={item}
                  columns={settings.columns}
                  areSomeRowExpandable={hasExpandableRows}
                  additionalProps={additionalProps}
                  sx={
                    isLast
                      ? {
                          "& > td": {
                            borderBottomColor: "transparent !important",
                          },
                        }
                      : undefined
                  }
                />
              );
            })}
          </React.Fragment>
        ))}
      </TableBody>
    );
  }

  return (
    <TableBody>
      {pagedData.map((item, i) => {
        const rowKey = settings.getRowId?.(item) ?? i;
        return (
          <AutoTableRow
            key={rowKey}
            settings={settings}
            row={item}
            columns={settings.columns}
            areSomeRowExpandable={hasExpandableRows}
            additionalProps={additionalProps}
          />
        );
      })}
    </TableBody>
  );
}
